import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { fmtEuro, STATUS_META, effectiveStatus, daysSince } from './paymentsShared';
import { PaymentTimeline } from './PaymentTimeline';

const sumBy = (nodes, st) => nodes.filter((n) => n.status === st).reduce((s, n) => s + (n.amount_net ?? n.amount ?? 0), 0);
const sumAll = (nodes) => nodes.reduce((s, n) => s + (n.amount_net ?? n.amount ?? 0), 0);

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d) ? null : d.toLocaleDateString('it-IT');
};

// Riga sintetica derivata SOLO dai dati disponibili. Priorità:
// 1. Da fatturare · 2. Fatturato non incassato · 3. prossimo Non sbloccato · 4. ultimo Incassato.
// Nessuna data/milestone inventata: se non determinabile → null (riga omessa).
const computeNextEvent = (nodes) => {
  const amt = (n) => fmtEuro(n.amount_net ?? n.amount);
  const byStatus = (st) => nodes.filter((n) => n.status === st);

  const daF = byStatus('da_fatturare');
  if (daF.length) {
    const pick = daF.slice().sort((a, b) => {
      const ta = a.unlocked_at ? new Date(a.unlocked_at).getTime() : Infinity;
      const tb = b.unlocked_at ? new Date(b.unlocked_at).getTime() : Infinity;
      return ta - tb;
    })[0];
    const d = daysSince(pick.unlocked_at);
    const detail = d != null ? `Da fatturare da ${d} ${d === 1 ? 'giorno' : 'giorni'}` : 'Da fatturare';
    return { tag: 'Da gestire', status: 'da_fatturare', text: `${pick.name} · ${amt(pick)} · ${detail}` };
  }

  const fatt = byStatus('fatturato');
  if (fatt.length) {
    const pick = fatt[0];
    return { tag: 'Da gestire', status: 'fatturato', text: `${pick.name} · ${amt(pick)} · Fatturato non incassato` };
  }

  const ns = byStatus('non_sbloccato');
  if (ns.length) {
    const pick = ns[0];
    return { tag: 'Prossimo', status: 'non_sbloccato', text: `${pick.name} · ${amt(pick)} · Non sbloccato` };
  }

  const inc = byStatus('incassato').filter((n) => n.paid_at);
  if (inc.length) {
    const pick = inc.slice().sort((a, b) => new Date(b.paid_at) - new Date(a.paid_at))[0];
    const dt = fmtDate(pick.paid_at);
    return { tag: 'Ultimo evento', status: 'incassato', text: `${pick.name} · ${amt(pick)} · Incassato${dt ? ` ${dt}` : ''}` };
  }

  return null;
};

const Metric = ({ label, value, cls }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wide font-medium text-[#737373] leading-tight">{label}</div>
    <div className={`text-[15px] font-bold leading-tight tabular-nums ${cls || 'text-[#3C3C3C]'}`}>{value}</div>
  </div>
);

// Micro-timeline sempre visibile: dot per stato reale delle milestone (dati già nel payload).
const MicroTimeline = ({ nodes }) => {
  if (!nodes || nodes.length === 0) return null;
  return (
    <div className="mt-3 flex items-center gap-0.5 overflow-x-auto" data-testid="micro-timeline" title="Stati milestone (dettaglio in Timeline pagamenti)">
      {nodes.map((n, i) => {
        const meta = STATUS_META[effectiveStatus(n)] || STATUS_META.da_fatturare;
        const tip = `${n.name} · ${meta.label} · netto ${fmtEuro(n.amount_net ?? n.amount)} · lordo ${fmtEuro(n.amount_gross ?? n.amount)}`;
        return (
          <React.Fragment key={n.id || i}>
            <span
              className={`w-3 h-3 rounded-full shrink-0 ring-1 ring-white ${meta.dot} cursor-help`}
              data-testid="micro-timeline-dot"
              title={tip}
            />
            {i < nodes.length - 1 && <span className="w-3 h-px bg-slate-300 shrink-0" />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const TABS = [
  { key: 'complessivo', label: 'Complessivo' },
  { key: 'lavori', label: 'Lavori' },
  { key: 'arredi', label: 'Arredi' },
  { key: 'extra_forniture', label: 'Extra/Forniture' },
];

export const ProjectPaymentCard = ({ project }) => {
  const [expanded, setExpanded] = useState(false);
  const [tab, setTab] = useState('complessivo');

  const all = [...project.lavori, ...project.arredi, ...project.extra_forniture];
  const valore = sumAll(all);
  const incassato = sumBy(all, 'incassato');
  const fatturatoNI = sumBy(all, 'fatturato');
  const daFatt = sumBy(all, 'da_fatturare');
  const nonSbl = sumBy(all, 'non_sbloccato');
  const perc = valore > 0 ? Math.round((incassato / valore) * 100) : 0;
  const nextEv = computeNextEvent(all);

  const tabNodes = tab === 'complessivo' ? all : project[tab] || [];

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`project-card-${project.project_number || project.project_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {project.project_id ? (
              <Link to={`/projects/${project.project_id}`} className="font-bold text-[#FF7819] hover:underline text-sm">{project.project_number || 'Progetto senza codice'}</Link>
            ) : <span className="font-bold text-[#3C3C3C] text-sm">{project.project_number || 'Progetto senza codice'}</span>}
            <span className="font-semibold text-[#3C3C3C] text-sm">{project.client_name || 'Cliente non associato'}</span>
          </div>
          {project.property_address && <div className="text-xs text-[#737373] truncate">{project.property_address}</div>}
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase text-[#a3a3a3]">Avanzamento incassi</div>
          <div className="text-lg font-bold text-emerald-600">{perc}%</div>
        </div>
      </div>

      <div className="relative h-1.5 bg-slate-100 rounded-full overflow-hidden my-3">
        <div className="absolute left-0 top-0 h-full bg-emerald-500" style={{ width: `${perc}%` }} />
      </div>

      <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
        <Metric label="Valore" value={fmtEuro(valore)} />
        <Metric label="Incassato" value={fmtEuro(incassato)} cls={STATUS_META.incassato.text} />
        <Metric label="Fatt. non incassato" value={fmtEuro(fatturatoNI)} cls={STATUS_META.fatturato.text} />
        <Metric label="Da fatturare" value={fmtEuro(daFatt)} cls={STATUS_META.da_fatturare.text} />
        <Metric label="Non sbloccato" value={fmtEuro(nonSbl)} cls={STATUS_META.non_sbloccato.text} />
      </div>

      {nextEv && (
        <div className="mt-3 flex items-center gap-2 text-xs" data-testid="project-next-event">
          <span className={`w-2 h-2 rounded-full shrink-0 ${STATUS_META[nextEv.status]?.dot || 'bg-slate-300'}`} />
          <span className="uppercase text-[10px] font-semibold text-[#a3a3a3] shrink-0">{nextEv.tag}</span>
          <span className="text-[#525252] truncate">{nextEv.text}</span>
        </div>
      )}

      <MicroTimeline nodes={all} />

      <button
        onClick={() => setExpanded((e) => !e)}
        data-testid={`project-expand-${project.project_number || project.project_id}`}
        className="mt-3 w-full flex items-center justify-center gap-1 text-xs text-[#737373] hover:text-[#FF7819] py-1.5 rounded-md hover:bg-slate-50 transition-colors"
      >
        {expanded ? <>Nascondi timeline <ChevronUp className="w-3.5 h-3.5" /></> : <>Timeline pagamenti <ChevronDown className="w-3.5 h-3.5" /></>}
      </button>

      {expanded && (
        <div className="mt-2 border-t border-slate-100 pt-3">
          <div className="flex gap-1 flex-wrap mb-3">
            {TABS.map((t) => {
              const cnt = t.key === 'complessivo' ? all.length : (project[t.key] || []).length;
              if (t.key !== 'complessivo' && cnt === 0) return null;
              return (
                <button key={t.key} onClick={() => setTab(t.key)}
                  data-testid={`project-tab-${t.key}`}
                  className={`px-2 py-0.5 text-[11px] rounded-full border ${tab === t.key ? 'bg-[#3C3C3C] text-white border-[#3C3C3C]' : 'bg-white text-[#525252] border-slate-300'}`}>
                  {t.label}{cnt > 0 && <span className="ml-1 opacity-70">{cnt}</span>}
                </button>
              );
            })}
          </div>
          <PaymentTimeline nodes={tabNodes} />
        </div>
      )}
    </div>
  );
};

export default ProjectPaymentCard;
